import React, { Component, Fragment } from "react";
import { Listbox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/solid";



/**
 * 下拉列表框控件
 * 使用方式1：
 * 传入keys 和 values，values作为下拉选项内容, prefix为显示在选中项之前的前缀，可省略
 * ```
 * <Selector
 *  //title，默认为当前prefix+选中项.text，如果指定且为function，则会替换掉默认的title格式
 *   title={()=>{}}
 *   keys={[]}
 *   values={[]}
 *   select={index}
 *   onChange={e=>{}} 
 *   prefix=""
 * />
 * ```
 * 使用方式2：
 * 传入options对象作为下拉选项内容
 * 
 * options中必备项为key和text
 * 
 * column可指定当前option所在的列号(columns>1时起作用)
 * 
 * checked可指定当前option是否被选中(optionType="check"时起作用)
 * ```
 * options = [
 *  {key:0, text:"item1",column:0,checked=false}
 *  {key:1, text:"item2",column:0,checked=false}
 *  {key:2, text:"item3",column:1,checked=false}
 *  {key:3, text:"item4",column:1,checked=false}
 * ]
 * ```
 * ```
 * <Selector
 *     //title，默认为当前prefix+选中项.text，如果指定且为function，则会替换掉默认的title格式
 *     title={()=>{}}
 *     //下拉框显示的列数，默认为1列
 *     columns={2}
 *     prefix="前缀："
 *     //文字内容对齐方式，默认为justify-end
 *     align="justify-center"
 *     //文字字体大小，默认为 text-m
 *     optFontStyle={"text-xs"}
 *     //通过options传入
 *     options={options}
 *     //下拉选项显示类型，text 或 check，如果为check，则select属性将失效，选中项将根据options中每一项的checked属性决定
 *     optionType="text" or "check"
 *     //当前选中项索引，optionType="text"时起作用
 *     select={index}
 *     onChange={e => {
 *     }}
 * />
 * ```
 */
export default class Selector extends Component {
    constructor(props) {
        super(props);
        const { optionType = "text" } = this.props;
        this.optionType = optionType;
    }

    onSelectChanged(e) {
        if (this.optionType == "check") {
            const v = e.value;
            if (v.checked == null) {
                v.checked = true;
            } else {
                v.checked = !v.checked;
            }
        }
        if (this.props.onChange != null) {
            this.props.onChange(e);
        }
    }

    #generateItems(select) {
        let optionItems = [];
        const { keys, values, options, optFontStyle, align, prefix, columns = 1 } = this.props;

        const optFS = optFontStyle == null ? " text-sm " : optFontStyle;
        const flexAlign = align == null ? " justify-end " : " " + align + " ";

        if (options == null) {
            //keys values 形式只支持单列
            for (let c = 0; c < keys.length; c++) {
                const k = keys[c];
                const v = values[c];
                let style = "";

                if (c == select) {
                    style = "text-gray-900 bg-gray-300";
                } else {
                    style = "text-gray-500"
                }

                optionItems.push(<Listbox.Option
                    className={"flex flex-row flex-1 items-center cursor-default select-none relative py-1 pl-2 pr-3 " + style}
                    key={c} value={c}>
                    <div className={" flex w-full " + optFS + flexAlign}>{v}</div>
                </Listbox.Option>);
            }
        } else {
            const columnItems = [];
            if (options[0].column == null) {
                //没有指定选项的column 平均分配
                const count = columns == 1 ? options.length : Math.ceil(options.length / columns);
                let c = 0;
                let n = 0;
                options.forEach(o => {
                    o.column = c;
                    if (++n >= count) {
                        c++;
                        n = 0;
                    }
                })
            }

            let i = 0;
            for (i = 0; i < columns; i++) {
                columnItems.push([])
            }

            let maxItems = 0;
            let cc = 1;
            for (i = 0; i < options.length; i++) {
                const o = options[i];
                if (o.column != null) {
                    cc = o.column;
                }
                const k = options[i].key;
                const v = options[i].text;
                let style = "";


                if ((this.optionType == "text" && i == select) || (this.optionType == "check" && options[i].checked)) {
                    style = "text-gray-900 bg-gray-300";
                } else {
                    style = "text-gray-500"
                }

                let itemDom = "";
                if (this.optionType == "text") {
                    itemDom = <div className={" flex w-full " + optFS + flexAlign}>{v}</div>;
                } else if (this.optionType == "check") {
                    itemDom = <div className={" flex w-full justify-start " + optFS}>
                        <div className=" w-[16px] h-[16px] border-[1px] border-gray-400 bg-white">
                            {options[i].checked ? <CheckIcon className="w-[16px] h-[16px]" /> : <></>}
                        </div>
                        <div className="flex w-full pl-1">{v}</div>
                    </div>;
                }

                columnItems[cc].push(<Listbox.Option
                    className={"flex flex-row flex-1 items-center cursor-default select-none relative py-1 pl-2 pr-3 " + style}
                    key={i} value={i}>
                    {itemDom}
                </Listbox.Option>);

                if (columnItems[cc].length > maxItems) {
                    maxItems = columnItems[cc].length;
                }
            }

            columnItems.forEach(item => {
                while (item.length < maxItems) {
                    item.push(<div key={++i} className="flex flex-1 items-center py-1 pl-2 pr-3">{" "}</div>);
                }
            })

            for (i = 0; i < columnItems.length; i++) {
                optionItems.push(<div key={i} className="flex flex-col flex-1">{columnItems[i]}</div>);
                if (i < columnItems.length - 1) {
                    optionItems.push(<div className="w-[1px] bg-gray-300" key={i + 100000} ></div>);
                }
            }

            optionItems = <div className="flex flex-row">{optionItems}</div>
            // for (let i = 0; i < l; i += columns) {
            //     let rowItems = [];
            //     for (let c = i; c < i + columns; c++) {
            //         if (c < (options == null ? keys.length : options.length)) {
            //             const k = options == null ? keys[c] : options[c].key;
            //             const v = options == null ? values[c] : options[c].text;
            //             let style = "";

            //             if (c == select) {
            //                 style = "text-gray-900 bg-gray-300";
            //             } else {
            //                 style = "text-gray-500"
            //             }

            //             rowItems.push(<Listbox.Option
            //                 className={"flex flex-row flex-1 items-center cursor-default select-none relative py-1 pl-2 pr-3 " + style}
            //                 key={c} value={c}>
            //                 <div className={" flex w-full " + optFS + flexAlign}>{v}</div>
            //             </Listbox.Option>);
            //         } else {
            //             rowItems.push(<div key={c} className="flex flex-1 pl-2 py-1 pr-3"></div>);
            //         }
            //         if (c < i + columns - 1) {
            //             rowItems.push(<div className="w-[1px] bg-gray-300" key={c + 100000} ></div>)
            //         }
            //     }
            //     optionItems.push(<div key={i} className="flex flex-row w-full">{rowItems}</div>)

            // }
        }
        return optionItems;
    }

    render() {
        /**
         * options {key,text, ...}
         */
        const { keys, values, options, optFontStyle, align, prefix = "", columns = 1,
            title = (_prefix, _itemSelect, _values) => {
                return _prefix + " " + _itemSelect.value;
            }
        } = this.props;
        let { select } = this.props;

        const optFS = optFontStyle == null ? " text-sm " : optFontStyle;
        const flexAlign = align == null ? " justify-end " : " " + align + " ";

        if (options == null) {
            if (keys == null || values == null || keys.length == 0 || values.length == 0) {
                return <div></div>
            }
        } else {
            if (options.length == 0) {
                return <div></div>
            }
        }

        if (select == null) {
            select = 0;
        }

        let longValue = options == null ? values[0] : options[0].text;

        if (options != null) {
            options.forEach(o => {
                if (o.text.length > longValue.length) {
                    longValue = o.text;
                }
            });
        } else {
            values.forEach(v => {
                if (v.length > longValue.length) {
                    longValue = v;
                }
            })
        }

        const selectItem =
            options == null ? { index: select, key: keys[select], value: values[select] }
                : { index: select, key: options[select].key, value: options[select].text }

        let optionItems = this.#generateItems(select);

        return (
            <div className='flex items-center w-full'>
                <div className='flex-1 justify-center items-center'>
                    <Listbox
                        value={selectItem.index} onChange={e => {
                            options == null ?
                                this.onSelectChanged({ index: e, key: keys[e], value: values[e] })
                                : this.onSelectChanged({ index: e, key: options[e].key, value: options[e] });
                        }}>
                        <div className="flex flex-1 h-0 overflow-hidden">{longValue}
                            <span className="flex items-center justify-end pointer-events-none">
                                <ChevronDownIcon
                                    className="w-5 h-5 text-gray-400"
                                    aria-hidden="true"
                                />
                            </span></div>
                        <div className="relative">
                            <Listbox.Button
                                className="relative w-full flex justify-end items-center cursor-default focus:outline-none focus-visible:ring-2 focus-visible:ring-opacity-75 focus-visible:ring-white focus-visible:ring-offset-orange-300 focus-visible:ring-offset-2 focus-visible:border-indigo-500 sm:text-sm"
                            >
                                <div className={"flex flex-1 w-full pl-5 " + flexAlign + optFS}>{
                                    //(prefix == null ? "" : prefix) + selectItem.value
                                    title(prefix, selectItem, options == null ? values : options)
                                }
                                </div>
                                <span className="flex items-center justify-end pointer-events-none">
                                    <ChevronDownIcon
                                        className="w-5 h-5 text-gray-400"
                                        aria-hidden="true"
                                    />
                                </span>
                            </Listbox.Button>
                            <Transition
                                as={Fragment}
                                leave="transition ease-in duration-100"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                            >
                                <Listbox.Options className="absolute z-10 w-full py-1 mt-1 overflow-auto text-base bg-gray-100 rounded-md shadow-lg max-h-64 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                    {optionItems}
                                </Listbox.Options>
                            </Transition>
                        </div>
                    </Listbox>

                </div>
            </div>
        );
    }
}